/**
 * @group Services
 * 
 * @summary
 * <blockquote>PostHog analytics service implementation</blockquote>
 * 
 * This service handles PostHog analytics event reporting with support for
 * environment-based sampling and custom configuration.
 * 
 * @example
 * ```typescript
 * import { PostHogService } from '@shared/reporting';
 * 
 * const service = new PostHogService(event);
 * await service.report();
 * ```
 */

import { tryCatchAsync, ok, err, convertToAppError, AsyncResult, AppError } from '@shared/utils';
import { HTTP_STATUS_NAMES } from '@shared/constants';
import { BaseEvent } from '../types/BaseEvent';
import { ReportingService } from '../interfaces/ReportingService';
import { getResource, ResourceConfig, ResourceReportingConfig } from '@shared/config';
import { EnvironmentReportingConfig } from '@shared/config/src/interfaces';
import { POSTHOG_FIELDS, POSTHOG_MESSAGES } from '../constants';
import { getPostHogInstance } from '../functions/getPostHogInstance';
import { formatMessage } from '../functions/formatMessage';
import { Logger } from '@shared/logger';
import { getEnvironment } from '@shared/utils';

/**
 * @internal
 */
export class PostHogService implements ReportingService {
  private event: BaseEvent;
  private config: ResourceReportingConfig | undefined;
  private currentEnvConfig: EnvironmentReportingConfig | undefined;
  private resourceConfig: ResourceConfig | null | undefined;
  private timestamp: string;
  private readonly initializationError: AppError | undefined;

  constructor(event: BaseEvent) {
    this.event = event;
    this.resourceConfig = getResource();
    this.config = this.resourceConfig?.reporting;
    this.timestamp = new Date(Date.now()).toISOString();
    
    // Get current environment and config
    const envResult = getEnvironment();
    if (envResult.isErr) {
      // Store error instead of throwing - will be returned in report()
      this.initializationError = envResult.getFail();
      return;
    }
    const currentEnv = envResult.getSuccess();
    
    this.currentEnvConfig = this.config?.[currentEnv];
  }

  /**
   * @summary
   * <blockquote>Internal PostHog reporting logic</blockquote>
   * 
   * @private
   */
  private async reportToPostHogInternal(): AsyncResult<void, AppError> {
    // Get PostHog instance for current environment
    const posthogResult = await getPostHogInstance();

    if (posthogResult.isErr) {
      return err(posthogResult.getFail());
    }

    const posthog = posthogResult.getSuccess();

    const result = await tryCatchAsync(async () => {
      // Check if we're in a browser environment
      if (typeof window !== 'undefined') {
        posthog.capture(this.event.name, {
          [POSTHOG_FIELDS.DISTINCT_ID]: this.event.distinctId,
          [POSTHOG_FIELDS.TIMESTAMP]: this.timestamp,
          [POSTHOG_FIELDS.PROPERTIES]: {
            ...this.event.props,
            [POSTHOG_FIELDS.RESOURCE]: this.resourceConfig,
            [POSTHOG_FIELDS.EVENT_ID]: this.event.id
          }
        });
      } else {
        // Node.js environment - use posthog-node
        Logger.info(formatMessage(POSTHOG_MESSAGES.CAPTURING_SERVER_EVENT, { event: this.event.name }));

        await posthog.capture({
          [POSTHOG_FIELDS.DISTINCT_ID_NODE]: this.event.distinctId,
          [POSTHOG_FIELDS.EVENT]: this.event.name,
          [POSTHOG_FIELDS.TIMESTAMP]: this.timestamp,
          [POSTHOG_FIELDS.PROPERTIES]: {
            ...this.event.props,
            [POSTHOG_FIELDS.RESOURCE]: this.resourceConfig,
            [POSTHOG_FIELDS.EVENT_ID]: this.event.id
          }
        });

        Logger.info(POSTHOG_MESSAGES.SERVER_EVENT_SUCCESS);
      }
    });

    return result.match({
      ok: () => ok(undefined),
      err: (error) => err(convertToAppError(
        POSTHOG_MESSAGES.REPORTING_FAILED,
        POSTHOG_MESSAGES.FAILED_TO_REPORT_EVENT,
        HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
      ))
    });
  }

  async report(): AsyncResult<void, AppError> {
    // Check for initialization error first
    if (this.initializationError) {
      return err(this.initializationError);
    }

    // Apply sampling logic if a sampling rate is specified
    if (this.currentEnvConfig?.samplingRate !== undefined) {
      const shouldSample = Math.random() < this.currentEnvConfig?.samplingRate;
      if (!shouldSample) {
        return ok(undefined);
      }
    }

    // Proceed with PostHog reporting
    const result = await this.reportToPostHogInternal();

    return result.match({
      ok: (value) => ok(value),
      err: (error) => err(error)
    });
  }
}